import type { ReactNode } from "react";

import type { SignInFailure } from "../auth/authority.ts";
import { Hero } from "./Hero.tsx";
import { Note, PhoneShell } from "./PhoneShell.tsx";

/**
 * Signing in, which `10 Rider App.dc.html` does not draw.
 *
 * ADR 0022 settled what an unauthenticated arrival does: it goes **straight to Keycloak's own page**
 * rather than being shown a form here. ADR 0014 and `CLAUDE.md` section 4 say why there could not be
 * a form anyway — Keycloak is the token issuer and owns credentials, so a password field in this
 * application would be talking to Keycloak and not to this platform's API, and
 * `POST /v1/customer-identities` takes an email and nothing else. That last point is sharper on this
 * channel than on any other: the rider is the one party a white-label operator might expect to
 * register inside their own application, and the platform deliberately does not let them.
 *
 * So this is not a sign-in form. It is the three arrivals that must **not** be redirected, because
 * redirecting any of them is an infinite loop:
 *
 *   - **after signing out** — the sign-out flow ends back on this origin with no session, so
 *     redirecting would take the rider straight back to the form they just left and "sign out" would
 *     mean "sign in again";
 *   - **a request that came back unanswered** — this tab has already been to the provider and
 *     returned with no code and no error, so leaving again would return the same way for as long as
 *     the browser allowed;
 *   - **a sign-in that failed** — the one that matters most. A rider whose account is not linked
 *     would never get to read the sentence telling them so.
 *
 * `auth/arrival.tsx` holds that decision, unchanged from the driver application's copy. This draws
 * the page it lands on, in this application's own vocabulary rather than a console's card.
 *
 * The words are this build's: the design supplies none.
 */

export interface SignInProps {
  readonly step: "start" | "authenticating" | "failed";
  /** Draws the confirmation, so signing out looks like it worked. */
  readonly afterSignOut: boolean;
  readonly providerName: string;
  readonly failure?: SignInFailure;
  readonly onContinue: () => void;
  readonly onStartAgain: () => void;
}

export function SignIn(props: SignInProps): ReactNode {
  if (props.step === "authenticating") {
    /*
     * The redirect in flight: discovery running, or the code coming back to be exchanged.
     *
     * Left as a page deliberately. This is the moment before the browser leaves, and a redirect here
     * would be a redirect during a redirect — which drops the one in progress.
     */
    return (
      <PhoneShell
        screen="journey screen--state"
        hero={<Hero kind="none" />}
        chip={{ class: "unknown", label: "signing in" }}
        title="Taking you to sign in"
        sub={`${props.providerName} is checking who you are.`}
        actions={[]}
        flag="wording not in the design"
      />
    );
  }

  if (props.step === "failed" && props.failure !== undefined) {
    const failure = props.failure;
    return (
      <PhoneShell
        screen="journey screen--state"
        hero={<Hero kind="none" />}
        chip={{ class: "failed", label: "sign-in failed" }}
        title={failure.headline}
        sub={failure.detail}
        actions={
          failure.retryable
            ? [
                { label: "Try again", glyph: "refresh", primary: true, onPress: props.onContinue },
                {
                  label: "Use a different account",
                  glyph: "person",
                  primary: false,
                  onPress: props.onStartAgain,
                },
              ]
            : // No retry button on a failure that can never succeed. A button a rider presses at a
              // kerbside and which cannot work is the surest way to teach button-mashing.
              []
        }
        flag="wording not in the design"
      >
        <Note kind="info" text={failure.code} />
      </PhoneShell>
    );
  }

  return (
    <PhoneShell
      screen="journey screen--state"
      hero={<Hero kind={props.afterSignOut ? "done" : "none"} />}
      chip={
        props.afterSignOut
          ? { class: "done", label: "signed out" }
          : { class: "idle", label: "signed out" }
      }
      title={props.afterSignOut ? "You're signed out" : "Sign in to book a ride"}
      sub={
        props.afterSignOut
          ? "Your session on this phone is over. Sign in again whenever you need a ride."
          : `${props.providerName} asks who you are, then brings you back here.`
      }
      actions={[
        { label: "Continue", glyph: "arrow_forward", primary: true, onPress: props.onContinue },
      ]}
      flag="wording not in the design"
    />
  );
}
